// src/API/FillInTheBlank/FillInTheBlankAPI.tsx
import apiClient from "../../services/apiClient";
import { mobileAppUri, dataLoaderUri } from "../API";
import { FormattedPracticeSentence } from "../../Lesson/FillInTheBlank/FillInTheBlank";

interface ApiResponseWrapper {
  // Define an interface for the expected API response structure
  items: FormattedPracticeSentence[];
  // Add other properties if your API wrapper includes them (e.g., count, nextPageToken)
}

export async function fetchExistingPracticeSentences(
  sectionName: string
): Promise<FormattedPracticeSentence[]> {
  if (!sectionName) {
    console.warn(
      "fetchExistingPracticeSentences called without a sectionName."
    );
    return [];
  }

  if (!mobileAppUri) {
    console.error("API base URI (mobileAppUri) is not configured in API.ts.");
    throw new Error("API base URI (mobileAppUri) is not configured.");
  }

  const encodedSectionName = encodeURIComponent(sectionName);
  const url = `${mobileAppUri}/api/v1/practice-sentences?section=${encodedSectionName}`;

  console.log("Attempting to fetch existing practice sentences from URL:", url);

  try {
    const responseData = await apiClient.get(`${mobileAppUri}/api/v1/practice-sentences`, {
      params: { section: sectionName }
    });

    if (responseData && Array.isArray(responseData.items)) {
      console.log(
        `Successfully fetched ${responseData.items.length} existing practice sentences for section "${sectionName}".`
      );
      return responseData.items; // Return the 'items' array
    } else {
      console.warn(
        `API response for section "${sectionName}" did not contain an 'items' array or was not in the expected format:`,
        responseData
      );
      return []; // Return empty array if 'items' is not found or not an array
    }
  } catch (error) {
    console.error(
      `Error in fetchExistingPracticeSentences for section "${sectionName}":`,
      error
    );
    throw error;
  }
}

/**
 * Creates a new practice sentence in the database.
 * @param sentenceData The full object for the new sentence.
 */
export async function createPracticeSentence(
  sentenceData: FormattedPracticeSentence
) {
  const url = `${dataLoaderUri}/api/v1/practice-sentences`;
  console.log("POSTing new sentence to:", url);

  try {
    return await apiClient.post(url, sentenceData); // Returns { message: "..." }
  } catch (error) {
    console.error("Error in createPracticeSentence:", error);
    throw error;
  }
}

/**
 * Updates an existing practice sentence.
 * @param sectionId The partition key of the sentence.
 * @param sentenceId The sort key of the sentence.
 * @param updateData An object containing only the fields to be updated.
 */
export async function updatePracticeSentence(
  sectionId: string,
  sentenceId: string,
  updateData: Partial<FormattedPracticeSentence>
) {
  const url = `${dataLoaderUri}/api/v1/practice-sentences/${sectionId}/${sentenceId}`;
  console.log(`PUTing update to: ${url}`);

  try {
    return await apiClient.put(url, updateData); // Returns { message: "..." }
  } catch (error) {
    console.error("Error in updatePracticeSentence:", error);
    throw error;
  }
}

/**
 * Deletes a practice sentence from the database.
 * @param sectionId The partition key of the sentence.
 * @param sentenceId The sort key of the sentence.
 */
export async function deletePracticeSentence(
  sectionId: string,
  sentenceId: string
) {
  const url = `${dataLoaderUri}/api/v1/practice-sentences/${sectionId}/${sentenceId}`;
  console.log(`DELETEing sentence at: ${url}`);

  try {
    return await apiClient.delete(url); // Returns { message: "..." }
  } catch (error) {
    console.error("Error in deletePracticeSentence:", error);
    throw error;
  }
}
